import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import LockIcon from '@mui/icons-material/Lock';
import KeyIcon from '@mui/icons-material/Key';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { SetCashDeskPinDialog } from './SetCashDeskPinDialog';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type {
  QuickFilter,
  RowAction,
  SearchField,
} from '../../components/data-grid/RouteDataGrid';
import {
  EntityDetailDialog,
  auditTrailFields,
} from '../../components/dialogs/EntityDetailDialog';
import type { DetailRow } from '../../components/dialogs/EntityDetailDialog';
import { ConfirmDialog } from '../../components/dialogs/ConfirmDialog';
import { usePrompt, useAlert } from '../../components/dialogs/DialogProvider';
import { fmtDateTime, fmtOrEmDash } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { UserSummary, UserDetail } from '../../api/types';
import {
  useMyPermissions,
  useUsersList,
  useUserDetail,
  useUserAction,
  useAdminResetPassword,
  useAdminResetTotp,
  useAdminResetLoginAttempts,
  useAdminClearPasswordHistory,
} from '../../api/queries';
import { AddUserDialog } from './AddUserDialog';
import { EditUserDialog } from './EditUserDialog';

export default function UsersPage() {
  usePageTitle('Users');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );
  const navigate = useNavigate();

  const list = useUsersList();
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const editUserDetail = useUserDetail(editUserId);
  const detail = useUserDetail(detailUserId);
  const [addOpen, setAddOpen] = useState(false);

  // Confirm-dialog state for the reactivate action (suspend / deactivate
  // use the imperative prompt flow below to capture a required reason).
  const [confirmTarget, setConfirmTarget] = useState<{
    user: UserSummary; action: 'suspend' | 'reactivate' | 'deactivate';
  } | null>(null);
  const userAction = useUserAction();
  const prompt = usePrompt();
  const alert = useAlert();

  // Suspend + Deactivate require a reason (recorded in the audit log).
  // Reactivate keeps the plain ConfirmDialog flow.
  const promptForReasonAndAct = useCallback(
    async (row: UserSummary, action: 'suspend' | 'deactivate') => {
      const verb = action === 'suspend' ? 'Suspend' : 'Deactivate';
      const reason = await prompt({
        title: `${verb} user`,
        message: <>{verb} <strong>{row.displayName || row.email}</strong>?</>,
        placeholder: 'Reason (required — recorded in the audit log)',
        multiline: true,
        confirmLabel: verb,
        destructive: action === 'deactivate',
      });
      if (reason === null) return; // cancelled
      if (!reason.trim()) {
        await alert({
          title: 'Reason required',
          message: 'Please enter a reason. It will be recorded in the audit log.',
          severity: 'error',
        });
        return;
      }
      userAction.mutate(
        { userId: row.id, action, reason: reason.trim() },
        {
          onError: (err) => alert({
            title: 'Action failed',
            message: (err as { message?: string }).message ?? `Could not ${action} user.`,
            severity: 'error',
          }),
        },
      );
    },
    [prompt, alert, userAction],
  );

  // Admin user-action targets.
  const [resetPwdTarget,      setResetPwdTarget]      = useState<UserSummary | null>(null);
  const [resetTotpTarget,     setResetTotpTarget]     = useState<UserSummary | null>(null);
  const [unlockTarget,        setUnlockTarget]        = useState<UserSummary | null>(null);
  const [clearHistoryTarget,  setClearHistoryTarget]  = useState<UserSummary | null>(null);
  const [pinTarget,           setPinTarget]           = useState<UserSummary | null>(null);

  const resetPwdMut     = useAdminResetPassword(resetPwdTarget?.id ?? '00000000-0000-0000-0000-000000000000');
  const resetTotpMut    = useAdminResetTotp(resetTotpTarget?.id ?? '00000000-0000-0000-0000-000000000000');
  const unlockMut       = useAdminResetLoginAttempts(unlockTarget?.id ?? '00000000-0000-0000-0000-000000000000');
  const clearHistoryMut = useAdminClearPasswordHistory(clearHistoryTarget?.id ?? '00000000-0000-0000-0000-000000000000');

  const columns = useMemo<GridColDef<UserSummary>[]>(() => [
    {
      field: 'displayName', headerName: 'User', flex: 1.5,
      renderCell: (p) => (
        <Stack sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.displayName || p.row.email}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.email}</Typography>
        </Stack>
      ),
    },
    {
      field: 'tenant', headerName: 'Tenant', width: 150, sortable: false,
      renderCell: (p) => (
        p.row.isSystem ? <Chip size="small" label="System" color="warning" /> :
        p.row.isArms ? <Chip size="small" label="ARMS" color="secondary" /> :
        p.row.isSupplierUser ? <Chip size="small" label="Supplier" variant="outlined" /> :
        <Chip size="small" label="Operator" variant="outlined" />
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusChip status={p.row.status} />,
    },
    {
      field: 'mfa', headerName: 'MFA', width: 110, sortable: false,
      renderCell: (p) => <MfaCell row={p.row} />,
    },
    {
      field: 'roleCount', headerName: 'Roles', type: 'number', width: 90,
      align: 'right', headerAlign: 'right',
    },
    {
      field: 'siteCount', headerName: 'Sites', type: 'number', width: 90,
      align: 'right', headerAlign: 'right',
      // 0 here means "all operator sites" — display the literal 0
      // unaltered so the column stays sortable; the user-facing nuance
      // is documented in the Sites detail row.
    },
    {
      field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value),
    },
  ], []);

  const searchFields = useMemo<SearchField[]>(() => [
    { kind: 'text',   key: 'displayName', label: 'Display name' },
    { kind: 'text',   key: 'email',       label: 'Email' },
    { kind: 'select', key: 'status',      label: 'Status', options: [
      { value: 'Active',     label: 'Active' },
      { value: 'PendingMfa', label: 'Pending MFA' },
      { value: 'Invited',    label: 'Invited' },
      { value: 'Suspended',  label: 'Suspended' },
      { value: 'Deactivated', label: 'Deactivated' },
    ] },
  ], []);

  const quickFilters = useMemo<QuickFilter<UserSummary>[]>(() => [
    { label: 'All',          filter: () => true },
    { label: 'Active',       filter: (r) => r.status === 'Active' },
    { label: 'Pending MFA',  filter: (r) => r.status === 'PendingMfa' || r.status === 'Invited' },
    { label: 'Suspended',    filter: (r) => r.status === 'Suspended' },
    { label: 'Deactivated',  filter: (r) => r.status === 'Deactivated' },
    { label: 'System users', filter: (r) => r.isSystem },
  ], []);

  const exportColumns = useMemo<ExportColumn<UserSummary>[]>(() => [
    { key: 'email',            label: 'Email' },
    { key: 'displayName',      label: 'Display Name' },
    { key: 'status',           label: 'Status' },
    { key: 'isArms',           label: 'ARMS',           format: (v) => (v ? 'Yes' : 'No') },
    { key: 'isSupplierUser',   label: 'Supplier user',  format: (v) => (v ? 'Yes' : 'No') },
    { key: 'operatorId',       label: 'Operator ID',    format: (v) => fmtOrEmDash(v) },
    { key: 'mfaPrimaryFactor', label: 'MFA factor',     format: (v) => fmtOrEmDash(v) },
    { key: 'pushEnabled',      label: 'Push enabled',   format: (v) => (v ? 'Yes' : 'No') },
    { key: 'passkeyCount',     label: 'Passkey count' },
    { key: 'roleCount',        label: 'Roles' },
    { key: 'siteCount',        label: 'Sites (0 = all)' },
    { key: 'createdAt',        label: 'Created',        format: (v) => fmtDateTime(v) },
    { key: 'updatedAt',        label: 'Updated',        format: (v) => fmtDateTime(v) },
  ], []);

  const actions = useMemo<RowAction<UserSummary>[]>(() => [
    {
      label: 'View',
      onClick: (row) => setDetailUserId(row.id),
    },
    {
      label: 'Edit',
      visible: () => has('users.edit'),
      onClick: (row) => setEditUserId(row.id),
    },
    {
      label: 'Reset password',
      visible: () => has('users.password-reset'),
      onClick: (row) => setResetPwdTarget(row),
    },
    {
      label: 'Reset 2FA',
      visible: () => has('users.mfa-reset'),
      onClick: (row) => setResetTotpTarget(row),
    },
    {
      label: 'Unlock account',
      visible: () => has('users.unlock'),
      onClick: (row) => setUnlockTarget(row),
    },
    {
      label: 'Clear password history',
      visible: () => has('users.password-reset'),
      onClick: (row) => setClearHistoryTarget(row),
    },
    {
      // Cash desk (plan Draft 1 W3): desk login factor for PIN-mechanism
      // sites. Human users only; the endpoint enforces the same.
      label: 'Set cash-desk PIN',
      visible: (row) => has('users.edit') && !row.isSystem,
      onClick: (row) => setPinTarget(row),
    },
    {
      label: 'Suspend',
      visible: (row) => has('users.suspend') && row.status === 'Active',
      onClick: (row) => { void promptForReasonAndAct(row, 'suspend'); },
    },
    {
      label: 'Reactivate',
      visible: (row) => has('users.suspend') && row.status === 'Suspended',
      onClick: (row) => setConfirmTarget({ user: row, action: 'reactivate' }),
    },
    {
      label: 'Deactivate',
      destructive: true,
      visible: (row) => has('users.suspend') &&
        row.status !== 'Deactivated' && row.status !== 'Active',
      onClick: (row) => { void promptForReasonAndAct(row, 'deactivate'); },
    },
    {
      // System users authenticate via Ed25519 challenge-response (see
      // Route_ServiceAuth_Methodology). Deep-links to the per-user
      // pubkey admin page.
      label: 'Manage API keys',
      visible: () => has('auth.system-users.admin'),
      onClick: (row) => navigate(`/admin/users/${row.id}/keys`),
    },
    {
      // Per-user audit timeline — sign-in events + every state change
      // attributable to this user. Same audit.view permission as the
      // global Audit page; RLS scopes operator users to their tenant.
      label: 'Audit history',
      visible: () => has('audit.view'),
      onClick: (row) => navigate(`/users/${row.id}/login-audit`),
    },
  ], [has, navigate, promptForReasonAndAct]);

  const detailFields = useMemo<DetailRow<UserDetail>[]>(() => [
    { label: 'Display name', render: (u) => u.displayName },
    { label: 'Email',        render: (u) => u.email },
    { label: 'Status',       render: (u) => <StatusChip status={u.status} /> },
    { label: 'Tenant kind',  render: (u) =>
      u.isSystem ? 'System (Ed25519 service account, platform-wide)' :
      u.isArms ? 'ARMS' :
      u.isSupplierUser ? `Supplier ${u.supplierId ?? ''}` :
      `Operator ${u.operatorId ?? ''}`
    },
    { label: 'Locale',       render: (u) => u.locale },
    { label: 'Time zone',    render: (u) => u.timeZone },
    { label: 'Phone',        render: (u) => fmtOrEmDash(u.phone) },

    { label: 'MFA factor',   separator: true, render: (u) => fmtOrEmDash(u.mfaPrimaryFactor) },
    { label: 'Push enabled', render: (u) => u.pushEnabled ? 'Yes' : 'No' },
    { label: 'Passkeys',     render: (u) => u.passkeyCount.toString() },
    { label: 'Locked until', render: (u) => fmtDateTime(u.lockedUntil) },
    { label: 'Admin unlock required', render: (u) => u.adminUnlockRequired ? 'Yes' : 'No' },

    { label: 'Roles', separator: true, render: (u) =>
      u.roles.length === 0 ? '—' : (
        <Stack spacing={0.5}>
          {u.roles.map((r) => (
            <Chip key={r.id} size="small" label={`${r.displayName} (${r.tier})`} variant="outlined" />
          ))}
        </Stack>
      ),
    },
    { label: 'Sites', render: (u) =>
      u.isSystem ? <em>Platform-wide (system service account)</em> :
      u.isArms ? <em>Platform-wide (ACES)</em> :
      u.siteIds.length === 0 ? <em>All operator sites</em> :
      `${u.siteIds.length} site(s) — see Edit for the list`,
    },

    ...auditTrailFields<UserDetail>(),
  ], []);

  const target = confirmTarget;
  const targetVerb = target?.action === 'suspend' ? 'suspend'
                   : target?.action === 'reactivate' ? 'reactivate'
                   : 'deactivate';
  const targetIsDestructive = target?.action === 'deactivate';

  const doAction = () => {
    if (!target) return;
    userAction.mutate(
      { userId: target.user.id, action: target.action },
      { onSuccess: () => setConfirmTarget(null) },
    );
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h4">Users</Typography>

        <RouteDataGrid<UserSummary>
          entityName="Users"
          queryKey={list.queryKey}
          listFn={list.listFn}
          columns={columns}
          searchFields={searchFields}
          quickFilters={quickFilters}
          exportColumns={exportColumns}
          reportTitle="ARMS — Users"
          actions={actions}
          primaryAction={
            has('users.create')
              ? { label: '+ Add user', onClick: () => setAddOpen(true) }
              : undefined
          }
          onRowClick={(row) => setDetailUserId(row.id)}
        />

        <EntityDetailDialog<UserDetail>
          open={detailUserId !== null}
          title={detail.data?.displayName ?? detail.data?.email ?? 'User'}
          isLoading={detail.isLoading}
          error={detail.error as { message?: string } | null}
          data={detail.data ?? null}
          fields={detailFields}
          onClose={() => setDetailUserId(null)}
        />

        <AddUserDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onAdded={() => {/* invalidation handled by the mutation hook */}}
        />

        <EditUserDialog
          open={editUserId !== null && !!editUserDetail.data}
          user={editUserDetail.data ?? null}
          onClose={() => setEditUserId(null)}
        />

        <SetCashDeskPinDialog
          userId={pinTarget?.id ?? null}
          userName={pinTarget?.displayName ?? ''}
          onClose={() => setPinTarget(null)}
        />

        <ConfirmDialog
          open={target !== null}
          title={`${capitalise(targetVerb)} user`}
          message={target ? (
            <>
              {targetVerb === 'deactivate'
                ? 'Permanently deactivate '
                : `${capitalise(targetVerb)} `}
              <strong>{target.user.displayName || target.user.email}</strong>?
              {targetVerb === 'deactivate' && ' This is non-reversible.'}
            </>
          ) : ''}
          confirmLabel={capitalise(targetVerb)}
          destructive={targetIsDestructive}
          busy={userAction.isPending}
          error={(userAction.error as { message?: string } | null)?.message}
          onConfirm={doAction}
          onCancel={() => setConfirmTarget(null)}
        />

        <ResetPasswordDialog
          target={resetPwdTarget}
          mutation={resetPwdMut}
          onClose={() => { resetPwdMut.reset(); setResetPwdTarget(null); }}
        />

        <ConfirmDialog
          open={resetTotpTarget !== null}
          title="Reset 2FA"
          message={resetTotpTarget ? (
            <>Reset 2FA for <strong>{resetTotpTarget.displayName || resetTotpTarget.email}</strong>?
            They will be walked through TOTP enrolment again on next sign-in.</>
          ) : ''}
          confirmLabel="Reset 2FA"
          destructive
          busy={resetTotpMut.isPending}
          error={(resetTotpMut.error as { message?: string } | null)?.message}
          onConfirm={() => resetTotpMut.mutate(undefined, { onSuccess: () => setResetTotpTarget(null) })}
          onCancel={() => setResetTotpTarget(null)}
        />

        <ConfirmDialog
          open={unlockTarget !== null}
          title="Unlock account"
          message={unlockTarget ? (
            <>Unlock <strong>{unlockTarget.displayName || unlockTarget.email}</strong>?
            Failed-login counter will be cleared and any active lockout removed.</>
          ) : ''}
          confirmLabel="Unlock"
          busy={unlockMut.isPending}
          error={(unlockMut.error as { message?: string } | null)?.message}
          onConfirm={() => unlockMut.mutate(undefined, { onSuccess: () => setUnlockTarget(null) })}
          onCancel={() => setUnlockTarget(null)}
        />

        <ConfirmDialog
          open={clearHistoryTarget !== null}
          title="Clear password history"
          message={clearHistoryTarget ? (
            <>Drop every entry in this user's password-history table?
            They'll be able to re-use a previously-used password on their
            next change.</>
          ) : ''}
          confirmLabel="Clear history"
          destructive
          busy={clearHistoryMut.isPending}
          error={(clearHistoryMut.error as { message?: string } | null)?.message}
          onConfirm={() => clearHistoryMut.mutate(undefined, { onSuccess: () => setClearHistoryTarget(null) })}
          onCancel={() => setClearHistoryTarget(null)}
        />
      </Stack>
    </>
  );
}

// ---------- Reset-password dialog (shows the generated temp password) ----------

type ResetPasswordDialogProps = {
  target: UserSummary | null;
  mutation: ReturnType<typeof useAdminResetPassword>;
  onClose: () => void;
};

function ResetPasswordDialog({ target, mutation, onClose }: ResetPasswordDialogProps) {
  const [confirming, setConfirming] = useState(true);
  // When the dialog opens for a new target, reset back to confirm mode.
  useEffect(() => {
    if (target) setConfirming(true);
  }, [target]);

  const open = target !== null;
  const temp = mutation.data?.temporaryPassword ?? '';
  const error = mutation.error as { message?: string } | null;

  const onConfirm = () => {
    setConfirming(false);
    mutation.mutate();
  };

  const onCopy = () => {
    if (temp) navigator.clipboard.writeText(temp).catch(() => { /* ignore */ });
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reset password</DialogTitle>
      <DialogContent dividers>
        {confirming ? (
          <Typography>
            Generate a new temporary password for{' '}
            <strong>{target?.displayName || target?.email}</strong>?{' '}
            They'll be forced to change it on their next sign-in.
          </Typography>
        ) : mutation.isPending ? (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress /></Stack>
        ) : error ? (
          <ErrorAlert>{error.message}</ErrorAlert>
        ) : (
          <Stack spacing={2}>
            <Alert severity="success">
              Temporary password generated. Copy it and deliver it to the user
              out-of-band — it's not stored anywhere recoverable.
            </Alert>
            <Stack direction="row" spacing={1} alignItems="center"
                   sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography
                sx={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 600, flex: 1, letterSpacing: 1 }}
              >
                {temp}
              </Typography>
              <Tooltip title="Copy to clipboard">
                <IconButton onClick={onCopy} aria-label="Copy temporary password">
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {confirming ? 'Cancel' : 'Close'}
        </Button>
        {confirming && (
          <Button onClick={onConfirm} variant="contained" disabled={mutation.isPending}>
            Generate temporary password
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function capitalise(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

function StatusChip({ status }: { status: string }) {
  const color = status === 'Active'      ? 'success'
              : status === 'Suspended'   ? 'warning'
              : status === 'Deactivated' ? 'default'
              : 'default';
  return <Chip size="small" label={status} color={color as 'success' | 'warning' | 'default'} />;
}

function MfaCell({ row }: { row: UserSummary }) {
  const factors: { label: string; icon: ReactNode }[] = [];
  if (row.mfaPrimaryFactor === 'totp') factors.push({ label: 'TOTP', icon: <KeyIcon fontSize="small" /> });
  if (row.passkeyCount > 0) factors.push({ label: `${row.passkeyCount} passkey`, icon: <VpnKeyIcon fontSize="small" /> });
  if (row.pushEnabled) factors.push({ label: 'Push', icon: <LockIcon fontSize="small" /> });

  if (factors.length === 0) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  return (
    <Stack direction="row" spacing={0.5}>
      {factors.map((f) => (
        <Tooltip key={f.label} title={f.label}>
          <Chip size="small" icon={f.icon as ReactElement} label={f.label} variant="outlined" />
        </Tooltip>
      ))}
    </Stack>
  );
}
